import {Injectable} from '@angular/core';
import {TokenService} from "../api/session/token.service";
import {Token} from "../api/session/token";
import {Persona} from "../model/personas/persona.model";
import {ApiService} from "../api/api.service";
import {InterfaceProviderService} from "../basic-entity-back/services/interface-provider.service";
import {JsonLdInterface} from "../api/entity-interface";
import {Usuario} from "../model/personas/usuario.model";
import {BehaviorSubject, EMPTY, Observable, of, Subject} from "rxjs";
import {catchError, map, switchMap, tap} from "rxjs/operators";
import {Router} from "@angular/router";

@Injectable({
    providedIn: 'root'
})
export class SessionService {
    private _sessionProfile: Persona | Usuario | null = null;
    private _sessionProfileBS = new BehaviorSubject(this._sessionProfile);
    private _profileChanges = new Subject<void>();
    private _requestProfileUpdate = new Subject<void>();
    public profileChanges = this._profileChanges.asObservable();

    public get token(): Token {
        return this._tokenService.token;
    }

    public get mercureToken(): string {
        return this._tokenService.mercureToken;
    }

    public get profile(): Persona | Usuario | null {
        return this._sessionProfile;
    }

    public get loggedIn(): boolean {
        return this._tokenService.checkLogin();
    }

    public get profileAsObservable(): Observable<Persona | Usuario | null> {
        if (this.profile) {
            this._sessionProfileBS.next(this.profile);
        }
        return this._sessionProfileBS.asObservable();
    }


    public get user(): Usuario | null {
        if (this._sessionProfile == null) {
            return null;
        } else if (this._sessionProfile instanceof Persona) {
            return this._sessionProfile.usuario;
        } else {
            return this._sessionProfile;
        }
    }

    constructor(private _tokenService: TokenService,
                private _interfaceProvider: InterfaceProviderService,
                private _router: Router,
                private _api: ApiService) {
        this._tokenService.sessionChanges.subscribe(() => {
            this._redirectIfTokenExpired();
            this._requestProfileUpdate.next();
        });
        this._initProfileUpdateTracking();
        this._requestProfileUpdate.next();
    }

    private _initProfileUpdateTracking() {
        this._requestProfileUpdate.pipe(
            switchMap(() => {
                if (this.isLogged()) {
                    return this._api.get<JsonLdInterface>('me')
                        .pipe(ApiService.sTakeBody(),
                            map(response => {
                                if (response['baja']) {
                                    return null;
                                }
                                const interf = this._interfaceProvider.interfaceForObject(response);
                                const res = interf ? interf.fromGetToModel(response) : null;
                                return res;
                            }),
                            // Un /me fallido (red caída, API caída o reiniciando, 401 por un token
                            // que el servidor ya no acepta...) NO se traduce en 'perfil nulo': eso
                            // dispararía el logout()+redirección de más abajo, echando de la sesión
                            // a alguien que sólo tuvo un corte transitorio -una regresión de
                            // comportamiento, no lo que hace hoy-. Se traga el error sin emitir
                            // ningún valor (EMPTY completa sin emitir), así que este intento de
                            // recarga en concreto se pierde pero la suscripción de más abajo sigue
                            // viva para la próxima. Sin este catchError, ApiService ya relanza el
                            // error tras el snackbar, y como el subscribe de abajo no tiene manejador
                            // de error, un solo fallo de /me cierra esa suscripción para siempre:
                            // _requestProfileUpdate se queda sin nadie escuchando y el perfil no se
                            // vuelve a recargar en lo que dure la pestaña -con lo que cualquier guard
                            // que espere una nueva emisión de profileChanges (AsociadosGuard) se
                            // queda esperando indefinidamente.
                            catchError(() => EMPTY));
                } else {
                    return of(null);
                }
            })
        ).subscribe(profile => {
            if (!profile && this._tokenService.token.token != null) {
                this._tokenService.logout();
                this._router.navigateByUrl('/login').then();
            }
            // El orden de estas tres líneas importa: _sessionProfile tiene que quedar puesto al día
            // ANTES de avisar por _profileChanges.next(). Cualquier guard que espere esa señal (hoy
            // AsociadosGuard) lee this.profile en el instante en que le llega el aviso; invertir el
            // orden haría que leyera el valor viejo y denegara en silencio a un cliente con permiso
            // real, sin ningún error que lo delate.
            this._sessionProfile = profile;
            this._sessionProfileBS.next(profile);
            this._profileChanges.next();
        });
    }

    /**
     * Replace the current session by the provided token
     */
    public useToken(token: string) {
        this._tokenService.useToken(token);
    }

    public hasRole(role: string): boolean {
        return this._tokenService.token.roles.includes(role);
    }

    public get sessionChanges() {
        return this._tokenService.sessionChanges;
    }

    public login(user: string, password: string): Promise<Token> {
        return this._tokenService.login({username: user, password: password});
    }

    public logout(): void {
        this._tokenService.logout();
    }

    public isLogged(): boolean {
        return this._tokenService.checkLogin();
    }

    public changeProfile(profile: Persona | Usuario): Promise<any> {
        return this._interfaceProvider
            .managerForResource(profile)
            .update(profile)
            .pipe(tap(() => this._requestProfileUpdate.next()))
            .toPromise();
    }

    /**
     * Fuerza una recarga de `/me`, sin pasar por `changeProfile()` (que además hace un PUT sobre el
     * propio perfil). Hace falta cuando algo AJENO al perfil -pero embebido en él, como
     * `Cliente.clientesAsociados`- cambia por su propio endpoint: por ejemplo, dar de baja una
     * clínica (`PUT /cliente_asociados/{id}`, ver asociados.component.ts) dejaría
     * `SessionService.profile` con la lista vieja -y esa clínica se seguiría ofreciendo en "Nuevo
     * pedido"- hasta el siguiente `/me`, que hoy sólo llega con el login o una recarga de página.
     */
    public refreshProfile(): void {
        this._requestProfileUpdate.next();
    }

    private _redirectIfTokenExpired() {
        if (this.token.isExpired()) {
            setTimeout(() => this._router.navigateByUrl('/login', /* Removed unsupported properties by Angular migration: queryParams. */ {})
                .catch(err => console.error(err)));
        }
    }
}
